import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDateRangeInput } from '@angular/material/datepicker';
import { MatHint } from '@angular/material/form-field';
import { MatLabel } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MemberDashboardService } from '../member-dashboard.service';

interface BreathworkEntry {
  date: string;
  type: 'Deep Breathing' | 'Box Breathing';
  count: number;
}

interface EntryDetail {
  cycles: number;
  createdDateTime: string;
  time: string;
}

interface DayData {
  date: Date;
  dateStr: string;
  dayLabel: string;
  deepBreathing: number;
  boxBreathing: number;
  deepBreathingEntries: EntryDetail[];
  boxBreathingEntries: EntryDetail[];
  month: string;
  monthIndex: number;
}

interface MonthGroup {
  monthName: string;
  startIndex: number;
  endIndex: number;
  dayCount: number;
}

interface YearlyDayData {
  date: Date;
  dateStr: string;
  hasActivity: boolean;
  type: 'deep' | 'box';
}

interface YearlyMonthData {
  monthName: string;
  monthIndex: number;
  days: YearlyDayData[];
}

@Component({
  selector: 'app-breathwork',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatDateRangeInput,
    MatHint,
    MatLabel,
    MatSelectModule
  ],
  templateUrl: './breathwork.component.html',
  styleUrl: './breathwork.component.css'
})
export class BreathworkComponent implements OnInit, OnChanges {
  @Input() memberId: any;
  @Input() startDate: any;
  @Input() endDate: any;

  // Date picker properties
  currentDate: any;
  previousDate: any;
  startDateBreathwork: string = formatDate(new Date(new Date().setDate(new Date().getDate() - 14)), 'yyyy-MM-dd', 'en');
  endDateBreathwork: string = formatDate(new Date(), 'yyyy-MM-dd', 'en');
  
  // Data properties
  mindfulnessData: any = null;
  isLoading: boolean = false;
  noDataFound: boolean = false;
  
  // Calendar heatmap data
  calendarDays: DayData[] = [];
  totalBreathworks: number = 0;
  monthGroups: MonthGroup[] = [];
  
  // Tooltip properties
  tooltipVisible: boolean = false;
  tooltipData: { entries: EntryDetail[]; type: string; date: string } | null = null;
  tooltipPosition: { x: number; y: number } = { x: 0, y: 0 };
  
  // Month range for timeline
  startMonth: string = '';
  endMonth: string = '';
  
  // Yearly Summary properties
  selectedYear: number = new Date().getFullYear();
  availableYears: number[] = [];
  yearlyDeepBreathingData: YearlyMonthData[] = [];
  yearlyBoxBreathingData: YearlyMonthData[] = [];
  yearlyDeepBreathingTotal: number = 0;
  yearlyBoxBreathingTotal: number = 0;
  // Weekday labels for yearly heatmap
  weekdayLabels: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  constructor(private dashboard: MemberDashboardService) {
    const today = new Date();
    const twoWeeksAgo = new Date(new Date().setDate(new Date().getDate() - 14));
    this.currentDate = today;
    this.previousDate = twoWeeksAgo;
  }

  ngOnInit(): void {
    // Initialize available years (current year - 2 to current year only - no future years)
    const currentYear = new Date().getFullYear();
    this.availableYears = [];
    for (let year = currentYear - 2; year <= currentYear+2; year++) {
      this.availableYears.push(year);
    }
    
    // Ensure selectedYear is not in the future
    if (this.selectedYear > currentYear) {
      this.selectedYear = currentYear;
    }
    
    // Use input dates if provided, otherwise use defaults
    if (this.startDate && this.endDate) {
      this.startDateBreathwork = this.startDate;
      this.endDateBreathwork = this.endDate;
      // Update date picker bound objects - parse yyyy-MM-dd format
      const startParts = this.startDate.split('-');
      const endParts = this.endDate.split('-');
      if (startParts.length === 3) {
        this.previousDate = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
      } else {
        this.previousDate = new Date(this.startDate);
      }
      if (endParts.length === 3) {
        this.currentDate = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
      } else {
        this.currentDate = new Date(this.endDate);
      }
    }
    
    if (this.memberId) {
      this.loadBreathworkData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Watch for changes to startDate and endDate inputs
    let shouldReload = false;
    
    if (changes['startDate'] && changes['startDate'].currentValue) {
      this.startDateBreathwork = changes['startDate'].currentValue;
      // Parse date string to Date object for date picker binding
      const dateParts = this.startDateBreathwork.split('-');
      if (dateParts.length === 3) {
        this.previousDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      } else {
        this.previousDate = new Date(this.startDateBreathwork);
      }
      shouldReload = true;
    }
    
    if (changes['endDate'] && changes['endDate'].currentValue) {
      this.endDateBreathwork = changes['endDate'].currentValue;
      // Parse date string to Date object for date picker binding
      const dateParts = this.endDateBreathwork.split('-');
      if (dateParts.length === 3) {
        this.currentDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      } else {
        this.currentDate = new Date(this.endDateBreathwork);
      }
      shouldReload = true;
    }
    
    // Only reload data if both dates are available and we have changes
    if (shouldReload && this.startDateBreathwork && this.endDateBreathwork && this.memberId) {
      this.processBreathworkData();
    }
  }

  loadBreathworkData(): void {
    if (!this.memberId) return;
    
    this.isLoading = true;
    
    this.dashboard.getMindfulnessData(this.memberId).subscribe({
      next: (response) => {
        this.mindfulnessData = response;
        this.processBreathworkData();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching breathwork data:', error);
        // Set empty data but still generate calendar to show date range with grey blocks
        this.mindfulnessData = null;
        this.processBreathworkData();
        this.isLoading = false;
      }
    });
  }

  processBreathworkData(): void {
    // Extract data from response - API returns { result: { "2025-06-23": { "Deep Breathing": [...], "Box Breathing": [...] } } }
    const result = this.mindfulnessData?.result || {};
    
    // Create a map to store entries by date with detailed information
    const entriesByDate: { 
      [key: string]: { 
        deepBreathing: number; 
        boxBreathing: number;
        deepBreathingEntries: EntryDetail[];
        boxBreathingEntries: EntryDetail[];
      } 
    } = {};
    
    // Date range for filtering - use date strings for comparison to avoid timezone issues
    const startDateStr = this.startDateBreathwork; // Already in 'yyyy-MM-dd' format
    const endDateStr = this.endDateBreathwork; // Already in 'yyyy-MM-dd' format
    
    // Process the data - result is an object with date keys
    if (result && typeof result === 'object') {
      Object.keys(result).forEach((dateKey: string) => {
        // Normalize date key format (ensure it's YYYY-MM-DD)
        const normalizedDateKey = dateKey.trim();
        
        // Filter by date range - compare date strings directly (YYYY-MM-DD format)
        // This avoids timezone issues when creating Date objects
        if (normalizedDateKey < startDateStr || normalizedDateKey > endDateStr) {
          return; // Skip entries outside the date range
        }
        
        const dateData = result[dateKey];
        
        // Initialize entry data for this date
        if (!entriesByDate[normalizedDateKey]) {
          entriesByDate[normalizedDateKey] = { 
            deepBreathing: 0, 
            boxBreathing: 0,
            deepBreathingEntries: [],
            boxBreathingEntries: []
          };
        }
        
        // Process Deep Breathing entries
        if (dateData['Deep Breathing'] && Array.isArray(dateData['Deep Breathing'])) {
          entriesByDate[normalizedDateKey].deepBreathing = dateData['Deep Breathing'].length;
          entriesByDate[normalizedDateKey].deepBreathingEntries = dateData['Deep Breathing'].map((entry: any) => ({
            cycles: entry.cycles || 0,
            createdDateTime: entry.createdDateTime || '',
            time: this.formatTime(entry.createdDateTime)
          }));
        }
        
        // Process Box Breathing entries
        if (dateData['Box Breathing'] && Array.isArray(dateData['Box Breathing'])) {
          entriesByDate[normalizedDateKey].boxBreathing = dateData['Box Breathing'].length;
          entriesByDate[normalizedDateKey].boxBreathingEntries = dateData['Box Breathing'].map((entry: any) => ({
            cycles: entry.cycles || 0,
            createdDateTime: entry.createdDateTime || '',
            time: this.formatTime(entry.createdDateTime)
          }));
        }
      });
    }

    // Always generate calendar days for the selected date range, even if there's no data
    this.generateCalendarDays(entriesByDate);
    
    // Calculate total breathworks (only for the selected date range)
    this.totalBreathworks = Object.values(entriesByDate).reduce((sum, day) => {
      return sum + day.deepBreathing + day.boxBreathing;
    }, 0);
    
    // Process yearly data for summary
    this.processYearlyData();
  }

  processYearlyData(): void {
    const result = this.mindfulnessData?.result || {};
    
    // Initialize yearly data structures
    this.yearlyDeepBreathingData = [];
    this.yearlyBoxBreathingData = [];
    this.yearlyDeepBreathingTotal = 0;
    this.yearlyBoxBreathingTotal = 0;
    
    // Create a map of all dates in the selected year
    const yearStart = new Date(this.selectedYear, 0, 1);
    const yearEnd = new Date(this.selectedYear, 11, 31);
    
    // Process data for the selected year
    const deepBreathingDays: { [key: string]: boolean } = {};
    const boxBreathingDays: { [key: string]: boolean } = {};
    
    if (result && typeof result === 'object') {
      Object.keys(result).forEach((dateKey: string) => {
        const date = new Date(dateKey + 'T00:00:00');
        if (date.getFullYear() === this.selectedYear) {
          const dateStr = formatDate(date, 'yyyy-MM-dd', 'en');
          const dateData = result[dateKey];
          
          // Check for Deep Breathing entries
          if (dateData['Deep Breathing'] && Array.isArray(dateData['Deep Breathing']) && dateData['Deep Breathing'].length > 0) {
            deepBreathingDays[dateStr] = true;
            this.yearlyDeepBreathingTotal += dateData['Deep Breathing'].length;
          }
          
          // Check for Box Breathing entries
          if (dateData['Box Breathing'] && Array.isArray(dateData['Box Breathing']) && dateData['Box Breathing'].length > 0) {
            boxBreathingDays[dateStr] = true;
            this.yearlyBoxBreathingTotal += dateData['Box Breathing'].length;
          }
        }
      });
    }
    
    // Generate yearly heatmap data
    this.generateYearlyHeatmap(deepBreathingDays, 'deep');
    this.generateYearlyHeatmap(boxBreathingDays, 'box');
  }

  generateYearlyHeatmap(activityDays: { [key: string]: boolean }, type: 'deep' | 'box'): void {
    const months: YearlyMonthData[] = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthStart = new Date(this.selectedYear, monthIndex, 1);
      const monthEnd = new Date(this.selectedYear, monthIndex + 1, 0);
      const days: YearlyDayData[] = [];
      
      // Get the day of week for the first day of the month (0 = Sunday, 1 = Monday, etc.)
      const firstDayOfWeek = monthStart.getDay();
      
      // Add empty cells for days before the first day of the month (to align with week structure)
      // We'll align to start from Sunday (0)
      for (let i = 0; i < firstDayOfWeek; i++) {
        days.push({
          date: new Date(0), // Placeholder
          dateStr: '',
          hasActivity: false,
          type: type
        });
      }
      
      // Add all days of the month
      const currentDate = new Date(monthStart);
      while (currentDate <= monthEnd) {
        const dateStr = formatDate(currentDate, 'yyyy-MM-dd', 'en');
        days.push({
          date: new Date(currentDate),
          dateStr: dateStr,
          hasActivity: activityDays[dateStr] || false,
          type: type
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      months.push({
        monthName: monthNames[monthIndex],
        monthIndex: monthIndex,
        days: days
      });
    }
    
    if (type === 'deep') {
      this.yearlyDeepBreathingData = months;
    } else {
      this.yearlyBoxBreathingData = months;
    }
  }

  onYearChange(): void {
    // Safety check: ensure selectedYear is not in the future
    const currentYear = new Date().getFullYear();
    if (this.selectedYear > currentYear) {
      this.selectedYear = currentYear;
    }
    this.processYearlyData();
  }

  isYearDisabled(year: number): boolean {
    const currentYear = new Date().getFullYear();
    // Disable future years
    return year > currentYear;
  }

  getMonthHeaderWidth(dayCount: number): number {
    // Calculate width based on number of days in 7-column grid
    // Each day cell is 11px, gap is 3px between cells
    // For a 7-column grid: width = (7 * 11px) + (6 * 3px) = 77px + 18px = 95px per week row
    // But we need to account for the actual number of columns used
    const weeks = Math.ceil(dayCount / 7);
    const width = (7 * 11) + (6 * 3); // Width of one full week row
    return width; // All months use the same width since we're using 7-column grid
  }

  formatTime(dateTimeString: string): string {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      return formatDate(date, 'h:mm a', 'en');
    } catch {
      return '';
    }
  }

  showTooltip(event: MouseEvent, day: DayData, type: 'deep' | 'box'): void {
    const entries = type === 'deep' ? day.deepBreathingEntries : day.boxBreathingEntries;
    if (entries.length === 0) return;
    
    this.tooltipData = {
      entries: entries,
      type: type === 'deep' ? 'Deep Breathing' : 'Box Breathing',
      date: day.dayLabel
    };
    
    // Position tooltip near cursor, but adjust if near screen edges
    const offset = 15;
    let x = event.clientX + offset;
    let y = event.clientY + offset;
    
    // Adjust if tooltip would go off right edge
    if (x + 250 > window.innerWidth) {
      x = event.clientX - 250 - offset;
    }
    
    // Adjust if tooltip would go off bottom edge
    if (y + 200 > window.innerHeight) {
      y = event.clientY - 200 - offset;
    }
    
    this.tooltipPosition = { x, y };
    this.tooltipVisible = true;
  }

  hideTooltip(): void {
    this.tooltipVisible = false;
    this.tooltipData = null;
  }

  updateTooltipPosition(event: MouseEvent): void {
    if (!this.tooltipVisible) return;
    
    const offset = 15;
    let x = event.clientX + offset;
    let y = event.clientY + offset;
    
    // Adjust if tooltip would go off right edge
    if (x + 250 > window.innerWidth) {
      x = event.clientX - 250 - offset;
    }
    
    // Adjust if tooltip would go off bottom edge
    if (y + 200 > window.innerHeight) {
      y = event.clientY - 200 - offset;
    }
    
    this.tooltipPosition = { x, y };
  }

  formatDateString(dateValue: any): string | null {
    if (!dateValue) return null;
    
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;
      return formatDate(date, 'Y-MM-d', 'en');
    } catch {
      return null;
    }
  }

  generateCalendarDays(entriesByDate: { 
    [key: string]: { 
      deepBreathing: number; 
      boxBreathing: number;
      deepBreathingEntries: EntryDetail[];
      boxBreathingEntries: EntryDetail[];
    } 
  }): void {
    this.calendarDays = [];
    
    // Parse dates more carefully to avoid timezone issues
    // Split the date string and create date in local timezone
    const startParts = this.startDateBreathwork.split('-');
    const endParts = this.endDateBreathwork.split('-');
    const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
    const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
    
    // Set months for timeline
    this.startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    this.endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    
    const currentDate = new Date(start);
    
    while (currentDate <= end) {
      // Format date consistently using the same method
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
      const dayOfMonth = currentDate.getDate();
      const monthName = currentDate.toLocaleDateString('en-US', { month: 'long' });
      const monthIndex = currentDate.getMonth();
      
      const dayData: DayData = {
        date: new Date(currentDate),
        dateStr: dateStr,
        dayLabel: `${dayOfMonth} ${dayOfWeek}`,
        deepBreathing: entriesByDate[dateStr]?.deepBreathing || 0,
        boxBreathing: entriesByDate[dateStr]?.boxBreathing || 0,
        deepBreathingEntries: entriesByDate[dateStr]?.deepBreathingEntries || [],
        boxBreathingEntries: entriesByDate[dateStr]?.boxBreathingEntries || [],
        month: monthName,
        monthIndex: monthIndex
      };
      
      this.calendarDays.push(dayData);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Calculate month groups for indicators
    this.calculateMonthGroups();
  }

  calculateMonthGroups(): void {
    this.monthGroups = [];
    
    if (this.calendarDays.length === 0) return;
    
    let currentMonth = this.calendarDays[0].month;
    let startIndex = 0;
    
    for (let i = 1; i < this.calendarDays.length; i++) {
      if (this.calendarDays[i].month !== currentMonth) {
        // End of current month group
        const dayCount = i - startIndex;
        this.monthGroups.push({
          monthName: currentMonth,
          startIndex: startIndex,
          endIndex: i - 1,
          dayCount: dayCount
        });
        
        // Start new month group
        currentMonth = this.calendarDays[i].month;
        startIndex = i;
      }
    }
    
    // Add the last month group
    const lastDayCount = this.calendarDays.length - startIndex;
    this.monthGroups.push({
      monthName: currentMonth,
      startIndex: startIndex,
      endIndex: this.calendarDays.length - 1,
      dayCount: lastDayCount
    });
  }

  getMonthIndicatorLeft(index: number): number {
    // Each column is 70px wide with 8px gap, so 78px per column
    return index * 78;
  }

  getMonthIndicatorWidth(dayCount: number): number {
    // Width = (dayCount * 70px) + ((dayCount - 1) * 8px gap)
    // Simplified: (dayCount * 78) - 8
    return (dayCount * 78) - 8;
  }

  filterByDate(startDateInput: HTMLInputElement, endDateInput: HTMLInputElement): void {
    if (startDateInput.value && endDateInput.value) {
      // Parse the date input and format consistently
      const startDate = new Date(startDateInput.value);
      const endDate = new Date(endDateInput.value);
      
      // Format dates as YYYY-MM-DD to match API format
      const start = formatDate(startDate, 'yyyy-MM-dd', 'en');
      const end = formatDate(endDate, 'yyyy-MM-dd', 'en');
      
      this.startDateBreathwork = start;
      this.endDateBreathwork = end;
      
      // Reprocess data with new date range
      this.processBreathworkData();
    }
  }

  getBlockClass(count: number): string {
    if (count === 0) return 'no-entries';
    return count > 0 ? 'has-entries' : '';
  }

  getBlockColor(type: 'deep' | 'box', count: number): string {
    if (count === 0) return 'grey';
    return type === 'deep' ? 'green' : 'pink';
  }

  getEntryText(count: number): string {
    if (count === 0) return '';
    return count === 1 ? '1 Entry' : `${count} Entry`;
  }
}

